import { fmtDDMMYY } from "../dateFormat";
import React, { useState, useEffect } from "react";
import { ChevronLeft, Eye, Check, Users, PenLine, BookOpen, Award, CheckCircle, Plus, Search, ArrowRight } from "lucide-react";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Av, Bg, Bt, Cd, Sec, Stat, Tbl, Inp, Sl, KpiStrip } from "../components";
import { SearchSelect } from "../components/SearchSelect";
import { notify } from "../notify";
import { useFormDefinition, ratingsOf, fieldOf } from "../formCatalog";

// The manager grid is drawn from the form template, so HR's edits reach
// this page too. The detail endpoint returns those five in camelCase.
const MGR_FIELD_TO_PROP = {
  quality_of_work: "qualityOfWork", productivity: "productivity",
  communication_skills: "communicationSkills", teamwork: "teamwork",
  ownership_accountability: "ownershipAccountability",
};

const awaitingFor = (a) => {
  if (a.status === "Pending") return "Employee";
  if (a.status === "Self Done") return a.mnm || "Manager";
  if (a.status === "Mgr Review") return "Centre Head";
  if (a.status === "CH Review") return "HR";
  return "—";
};

export const AppraisalPage = ({ apData = [], apCycle, apMy = [], apTeam = [], apQuestions = [], employees = [], myEmployeeId, role, hashQuery, onRefresh, can = () => true }) => {
  const [apTab, setApTab] = useState("my");
  const [apDet, setApDet] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [showNewAp, setShowNewAp] = useState(false);
  const [newApTarget, setNewApTarget] = useState("");
  const [creatingAp, setCreatingAp] = useState(false);
  const [apSearch, setApSearch] = useState("");
  const [apStatusFilter, setApStatusFilter] = useState("");
  const canPickEmployee = can('appraisal', 'all', 'open_for_employee', 'edit');
  const { def: apForm } = useFormDefinition("appraisal");
  const mgrRatings = ratingsOf(apForm, "manager");
  const mgrLabel = (id, fallback) => fieldOf(apForm, "manager", id)?.label || fallback;

  // Deep link from a notification ("Please complete your self-assessment",
  // "X's appraisal was reviewed by their manager", ...) — #appraisal?id=<id>
  // opens that specific appraisal instead of leaving the viewer on the list.
  useEffect(() => {
    if (!hashQuery?.id) return;
    api.get("/api/appraisals/" + hashQuery.id).then(setApDet).catch(() => notify("Couldn't open that appraisal", { title: "Error", variant: "error" }));
  }, [hashQuery?.id]);

  useEffect(() => { setApSearch(""); setApStatusFilter(""); }, [apTab]);

  const createAppraisal = async (employee_id) => {
    setCreatingAp(true);
    try {
      const created = await api.post("/api/appraisals/create", employee_id ? { employee_id } : {});
      onRefresh?.();
      setShowNewAp(false); setNewApTarget("");
      // Jump straight into the form instead of leaving them on the list, and
      // skip a "success" popup here — nothing is actually done yet (the form
      // is still empty), so landing on it is confirmation enough. The real
      // "submitted successfully" message fires later, from submitSelf, once
      // they've actually filled it in.
      if (created?.id) {
        const detail = await api.get("/api/appraisals/" + created.id);
        setEditVals({});
        setApDet(detail);
      }
    } catch (e) { notify(e.message || "Failed to start appraisal", { title: "Error", variant: "error" }); }
    finally { setCreatingAp(false); }
  };

  const AP = apData;
  const cyc = apCycle;
  const isMgr = can('appraisal', 'team', null, 'view');
  // Role alone isn't enough to show an editable review form — a "manager"
  // system_role only lets you edit records where you're the actual assigned
  // manager (matches the backend's own check), and nobody can review/approve
  // their own appraisal at any stage regardless of role.
  const isSelf = (rec) => rec.eid === myEmployeeId;
  const canEditMgr = (rec) => !isSelf(rec) && (can('appraisal', 'manager_stage', null, 'edit') || rec.mid === myEmployeeId);
  const canEditCh = (rec) => !isSelf(rec) && can('appraisal', 'ch_stage', null, 'edit');
  const canEditHr = (rec) => !isSelf(rec) && can('appraisal', 'hr_stage', null, 'edit');

  const stClrAp = (s) => s === "Done" ? K.g : s === "Mgr Review" ? K.bl : s === "CH Review" ? K.tl : s === "Self Done" ? K.pu : s === "Ineligible" ? K.t3 : K.a;

  const eligible = AP.filter(a => a.eligible !== false && a.status !== "Ineligible");
  const selfDone = AP.filter(a => ["Self Done", "Mgr Review", "CH Review", "Done"].includes(a.status));
  const mgrDone = AP.filter(a => ["Mgr Review", "CH Review", "Done"].includes(a.status));
  const chDone = AP.filter(a => ["CH Review", "Done"].includes(a.status));
  const done = AP.filter(a => a.status === "Done");

  // 4-step cycle stepper — each stage's % is against everyone eligible
  // (not against the previous stage's count), so a stalled early stage
  // shows up as a low number on every later step too, instead of hiding
  // behind a deceptively "complete" % of a tiny carried-forward pool.
  const stepPct = (n) => eligible.length > 0 ? Math.round(n / eligible.length * 100) : 0;
  const cycleSteps = [
    { l: "Self Evaluation", sub: "Employees rate KRAs & accomplishments", pct: stepPct(selfDone.length), c: K.p },
    { l: "Manager Assessment", sub: "Managers review & calibrate ratings", pct: stepPct(mgrDone.length), c: K.bl },
    { l: "Centre Head Review", sub: "Departmental normalization & bell-curve", pct: stepPct(chDone.length), c: K.tl },
    { l: "HR / Final Sign-off", sub: "Letters, increment & bonus roll-out", pct: stepPct(done.length), c: K.g },
  ];
  const cycleCurrentStep = cycleSteps.findIndex(s => s.pct < 100);

  const matchesSearch = (r) => !apSearch.trim() || `${r.enm} ${r.rl} ${r.dept}`.toLowerCase().includes(apSearch.trim().toLowerCase());
  const matchesStatus = (r) => !apStatusFilter || r.status === apStatusFilter;
  const apTeamFiltered = apTeam.filter(r => matchesSearch(r) && matchesStatus(r));
  const apAllFiltered = AP.filter(r => matchesSearch(r) && matchesStatus(r));

  const submitSelf = async (apId, vals) => {
    setSavingId(apId);
    try {
      if (vals.self_doc_file) {
        const fd = new FormData();
        fd.append("self_rating", vals.self_rating);
        fd.append("self_answers", JSON.stringify(vals.self_answers || {}));
        fd.append("self_appraisal_document", vals.self_doc_file);
        await api.upload("/api/appraisals/" + apId + "/self", fd, "PUT");
      } else {
        await api.put("/api/appraisals/" + apId + "/self", { self_rating: vals.self_rating, self_answers: vals.self_answers || {} });
      }
      notify("Self assessment submitted successfully", { title: "Saved", variant: "success" });
      onRefresh?.(); setApDet(null);
    } catch (e) { notify(e.message || "Failed to submit self assessment", { title: "Error", variant: "error" }); }
    finally { setSavingId(null); }
  };
  const submitMgr = async (apId, vals) => {
    setSavingId(apId);
    try {
      await api.put("/api/appraisals/" + apId + "/manager", vals);
      notify("Manager review submitted successfully", { title: "Saved", variant: "success" });
      onRefresh?.(); setApDet(null);
    } catch (e) { notify(e.message || "Failed to submit manager review", { title: "Error", variant: "error" }); }
    finally { setSavingId(null); }
  };
  const submitCh = async (apId, vals) => {
    setSavingId(apId);
    try {
      await api.put("/api/appraisals/" + apId + "/ch", vals);
      notify("Centre head review submitted successfully", { title: "Saved", variant: "success" });
      onRefresh?.(); setApDet(null);
    } catch (e) { notify(e.message || "Failed to submit centre head review", { title: "Error", variant: "error" }); }
    finally { setSavingId(null); }
  };
  const submitApprove = async (apId, vals) => {
    setSavingId(apId);
    try {
      await api.put("/api/appraisals/" + apId + "/approve", vals || {});
      notify("Appraisal approved and finalized", { title: "Saved", variant: "success" });
      onRefresh?.(); setApDet(null);
    } catch (e) { notify(e.message || "Failed to approve appraisal", { title: "Error", variant: "error" }); }
    finally { setSavingId(null); }
  };

  if (apDet) {
    const d = apDet;
    return (
      <div>
        <Bt v="ghost" s="sm" icon={ChevronLeft} onClick={() => setApDet(null)} style={{ marginBottom: 10 }}>Back</Bt>
        <div className="grid gap-3 grid-cols-1 lg:[grid-template-columns:1fr_260px]">
          <div>
            <Cd style={{ marginBottom: 12 }}>
              <div className="flex items-center justify-between mb-[14px]">
                <div className="flex items-center gap-[10px]"><Av nm={d.enm} sz={36} /><div><div className="text-[15px] font-bold text-t0">{d.eid} - {d.enm}</div><div className="text-[14px] text-t3">{d.rl} · {d.dept} · {d.ofc}</div></div></div>
                <Bg c={stClrAp(d.status)} dot>{d.status}</Bg>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px] mb-[14px]">
                <div><div className="text-[12px] text-t3">DOJ</div><span className="text-[14px] font-semibold text-t0">{d.doj || "—"}</span></div>
                <div><div className="text-[12px] text-t3">Tenure</div><span className="text-[14px] font-semibold text-t0">{d.tenure ? Math.round(d.tenure / 30) + "m (" + d.tenure + "d)" : "—"}</span></div>
                <div><div className="text-[12px] text-t3">Manager</div><span className="text-[14px] font-semibold text-t0">{d.mnm || "—"}</span></div>
              </div>
            </Cd>

            <Cd style={{ marginBottom: 12 }}>
              <Sec icon={PenLine} title="Self Assessment" />
              {d.status === "Pending" ? <div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">Self Rating (1-5)</div><Inp placeholder="4.0" value={editVals.self_rating || ""} onChange={v => setEditVals(prev => ({ ...prev, self_rating: v }))} type="number" /></div>
                {apQuestions.map(q => {
                  const val = (editVals.self_answers || {})[q.id] || "";
                  const setVal = (v) => setEditVals(prev => ({ ...prev, self_answers: { ...(prev.self_answers || {}), [q.id]: v } }));
                  return <div key={q.id} className="mb-2">
                    <div className="text-[14px] text-t2 mb-[3px]">{q.label}{q.required && <span style={{ color: K.r }}> *</span>}</div>
                    {q.question_type === "select" ? <Sl options={q.options || []} value={val} onChange={setVal} />
                      : q.question_type === "file" ? <input type="file" onChange={e => setEditVals(prev => ({ ...prev, self_doc_file: e.target.files[0] }))} className="text-[14px] text-t1" />
                      : q.question_type === "text" ? <Inp placeholder="Your answer..." value={val} onChange={setVal} />
                      : <textarea value={val} onChange={e => setVal(e.target.value)} placeholder="Your answer..." className="w-full min-h-[60px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border" />}
                  </div>;
                })}
                <Bt disabled={savingId === d.id} onClick={() => {
                  if (!editVals.self_rating) { notify("Rating required", { title: "Error", variant: "error" }); return; }
                  const missing = apQuestions.find(q => q.required && q.question_type !== "file" && !String((editVals.self_answers || {})[q.id] || "").trim());
                  if (missing) { notify(`"${missing.label}" is required`, { title: "Error", variant: "error" }); return; }
                  submitSelf(d.id, editVals);
                }}>{savingId === d.id ? "Submitting..." : "Submit Self Assessment"}</Bt>
              </div> : <div>
                <div className="grid grid-cols-2 gap-[10px]">
                  <div><div className="text-[12px] text-t3">Self Rating</div><span className="text-[18px] font-extrabold text-p">{d.sr ? d.sr + "/5" : "—"}</span></div>
                  <div><div className="text-[12px] text-t3">Status</div><Bg c={K.g} sm dot>Submitted</Bg></div>
                </div>
                {apQuestions.map(q => (d.selfAnswers || {})[q.id] ? <div key={q.id} className="mt-2"><div className="text-[12px] text-t3">{q.label}</div><div className="text-[14px] text-t1 py-1 whitespace-pre-wrap">{(d.selfAnswers || {})[q.id]}</div></div> : null)}
                {d.selfDoc && <div className="mt-2"><div className="text-[12px] text-t3">Self Appraisal Document</div><a href={uploadUrl(d.selfDoc)} target="_blank" rel="noreferrer" className="text-[14px] font-semibold" style={{ color: K.p }}>View document</a></div>}
              </div>}
            </Cd>

            {d.selfView && (d.status === "Self Done" || d.status === "Mgr Review" || d.status === "CH Review") && (
              <Cd style={{ marginBottom: 12 }}>
                <div className="flex items-center gap-2 p-1">
                  <Eye size={16} color={K.t3} />
                  <span className="text-[14px] text-t2">Your self appraisal is with {awaitingFor(d).toLowerCase()} for review. You'll be notified once it's finalized.</span>
                </div>
              </Cd>
            )}

            {!d.selfView && <Cd style={{ marginBottom: 12 }}>
              <Sec icon={BookOpen} title="Manager Review" />
              {d.status === "Self Done" && canEditMgr(d) && !d.mr ? <div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">Manager Rating (1-5)</div><Inp placeholder="4.0" value={editVals.manager_rating || ""} onChange={v => setEditVals(prev => ({ ...prev, manager_rating: v }))} type="number" /></div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">Comments</div><textarea value={editVals.manager_comments || ""} onChange={e => setEditVals(prev => ({ ...prev, manager_comments: e.target.value }))} placeholder="Manager feedback..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-2">
                  {mgrRatings.map(f => (
                    <div key={f.id}>
                      <div className="text-[14px] text-t2 mb-[3px]">{f.label}</div>
                      <Sl options={[{ v: "", l: "Select" }, ...(f.options || []).map(o => ({ v: o, l: o }))]} value={editVals[f.id] || ""} onChange={v => setEditVals(prev => ({ ...prev, [f.id]: v }))} />
                    </div>
                  ))}
                </div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">{mgrLabel("areas_of_improvement", "Areas of Improvement")}</div><textarea value={editVals.areas_of_improvement || ""} onChange={e => setEditVals(prev => ({ ...prev, areas_of_improvement: e.target.value }))} placeholder="Where should they focus next year..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border" /></div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">{mgrLabel("training_required", "Training Required")}</div><textarea value={editVals.training_required || ""} onChange={e => setEditVals(prev => ({ ...prev, training_required: e.target.value }))} placeholder="Training that would help..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-2">
                  <div><div className="text-[14px] text-t2 mb-[3px]">Recommendation</div><Sl options={[{ v: "", l: "Select..." }, { v: "Promotion", l: "Promotion" }, { v: "Increment", l: "Increment" }, { v: "No Change", l: "No Change" }, { v: "PIP", l: "Performance Improvement Plan" }]} value={editVals.manager_recommendation || ""} onChange={v => setEditVals(prev => ({ ...prev, manager_recommendation: v }))} /></div>
                  <div><div className="text-[14px] text-t2 mb-[3px]">{mgrLabel("manager_increment_pct", "Increment (%)")}</div><Inp placeholder="10" type="number" value={editVals.manager_increment_pct || ""} onChange={v => setEditVals(prev => ({ ...prev, manager_increment_pct: v }))} /></div>
                </div>
                <Bt disabled={savingId === d.id} onClick={() => { if (!editVals.manager_rating) { notify("Rating required", { title: "Error", variant: "error" }); return; } submitMgr(d.id, editVals); }}>{savingId === d.id ? "Submitting..." : "Submit Manager Review"}</Bt>
              </div> : d.mr ? <div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px]">
                  <div><div className="text-[12px] text-t3">Rating</div><span className="text-[18px] font-extrabold text-bl">{d.mr}/5</span></div>
                  <div><div className="text-[12px] text-t3">Recommendation</div><Bg c={d.rec === "Promotion" ? K.g : d.rec === "Increment" ? K.bl : d.rec === "PIP" ? K.r : K.t3} sm>{d.rec || "—"}</Bg></div>
                  <div><div className="text-[12px] text-t3">Manager</div><span className="text-[14px] text-t1">{d.mnm}</span></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-[10px] mt-[10px]">
                  {mgrRatings.map(f => d[MGR_FIELD_TO_PROP[f.id]] ? (
                    <div key={f.id}><div className="text-[12px] text-t3">{f.label}</div><span className="text-[14px] text-t1">{d[MGR_FIELD_TO_PROP[f.id]]}</span></div>
                  ) : null)}
                </div>
                {d.improve && <div className="mt-[6px]"><div className="text-[12px] text-t3">Areas of Improvement</div><div className="text-[14px] text-t1 whitespace-pre-wrap">{d.improve}</div></div>}
                {d.trainingRequired && <div className="mt-[6px]"><div className="text-[12px] text-t3">Training Required</div><div className="text-[14px] text-t1 whitespace-pre-wrap">{d.trainingRequired}</div></div>}
                {d.mgrIncrementPct != null && <div className="mt-[6px]"><div className="text-[12px] text-t3">Increment suggested</div><div className="text-[14px] text-t1">{d.mgrIncrementPct}%</div></div>}
                {d.mgrComm && <div className="mt-[6px]"><div className="text-[12px] text-t3">Comments</div><div className="text-[14px] text-t1">{d.mgrComm}</div></div>}
              </div> : <div className="text-[14px] text-t3 p-2">{d.status === "Pending" ? "Awaiting employee self-assessment" : "Awaiting manager review"}</div>}
            </Cd>}

            {!d.selfView && <Cd style={{ marginBottom: 12 }}>
              <Sec icon={Award} title="Centre Head Review" />
              {d.status === "Mgr Review" && canEditCh(d) && !d.chr ? <div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">CH Rating (1-5)</div><Inp placeholder="4.0" value={editVals.ch_rating || ""} onChange={v => setEditVals(prev => ({ ...prev, ch_rating: v }))} type="number" /></div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">Final Rating Override (optional)</div><Inp placeholder="Auto-calculated if empty" value={editVals.final_rating || ""} onChange={v => setEditVals(prev => ({ ...prev, final_rating: v }))} type="number" /></div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">Comments</div><Inp placeholder="CH comments..." value={editVals.ch_comments || ""} onChange={v => setEditVals(prev => ({ ...prev, ch_comments: v }))} /></div>
                <Bt disabled={savingId === d.id} onClick={() => submitCh(d.id, editVals)}>{savingId === d.id ? "Submitting..." : "Submit CH Review"}</Bt>
              </div> : d.chr ? <div>
                <div className="grid grid-cols-2 gap-[10px]">
                  <div><div className="text-[12px] text-t3">CH Rating</div><span className="text-[18px] font-extrabold text-tl">{d.chr}/5</span></div>
                  <div><div className="text-[12px] text-t3">Final Rating</div><span className="text-[18px] font-extrabold text-g">{d.fr}/5</span></div>
                </div>
                {d.chComm && <div className="mt-[6px]"><div className="text-[12px] text-t3">Comments</div><div className="text-[14px] text-t1">{d.chComm}</div></div>}
              </div> : <div className="text-[14px] text-t3 p-2">{d.status === "Mgr Review" ? "Awaiting Centre Head review" : "Awaiting manager review"}</div>}
            </Cd>}

            {!d.selfView && (d.status === "CH Review" || d.status === "Done") && <Cd>
              <Sec icon={CheckCircle} title="HR Final Approval" />
              {d.status === "CH Review" && canEditHr(d) ? <div>
                <div className="mb-2"><div className="text-[14px] text-t2 mb-[3px]">HR Comments (optional)</div><Inp placeholder="Final remarks..." value={editVals.hr_comments || ""} onChange={v => setEditVals(prev => ({ ...prev, hr_comments: v }))} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-[8px] mb-2">
                  <div><div className="text-[14px] text-t2 mb-[3px]">Hike (%)</div><Inp placeholder="10" type="number" value={editVals.hike || ""} onChange={v => setEditVals(prev => ({ ...prev, hike: v }))} /></div>
                  <div><div className="text-[14px] text-t2 mb-[3px]">Revised CTC</div><Inp placeholder="800000" type="number" value={editVals.revised_ctc || ""} onChange={v => setEditVals(prev => ({ ...prev, revised_ctc: v }))} /></div>
                  <div><div className="text-[14px] text-t2 mb-[3px]">Promoted Designation</div><Inp placeholder="Senior Executive" value={editVals.promoted_designation || ""} onChange={v => setEditVals(prev => ({ ...prev, promoted_designation: v }))} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-[8px] mb-2">
                  <div><div className="text-[14px] text-t2 mb-[3px]">Date of Confirmation</div><input type="date" value={editVals.date_of_confirmation || ""} onChange={e => setEditVals(prev => ({ ...prev, date_of_confirmation: e.target.value }))} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
                  <div><div className="text-[14px] text-t2 mb-[3px]">Appraisal Due Date</div><input type="date" value={editVals.appraisal_due_date || ""} onChange={e => setEditVals(prev => ({ ...prev, appraisal_due_date: e.target.value }))} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
                  <div><div className="text-[14px] text-t2 mb-[3px]">Net Salary</div><Inp placeholder="55000" type="number" value={editVals.net_salary || ""} onChange={v => setEditVals(prev => ({ ...prev, net_salary: v }))} /></div>
                </div>
                <Bt v="success" disabled={savingId === d.id} onClick={() => submitApprove(d.id, editVals)}>{savingId === d.id ? "Approving..." : "Approve & Finalize"}</Bt>
              </div> : d.status === "Done" ? <div>
                <div className="flex items-center gap-2 p-2">
                  <CheckCircle size={20} color={K.g} /><span className="text-[14px] font-bold text-g">Appraisal Finalized — {d.fr}/5</span>
                </div>
                {(d.hike != null || d.revisedCtc != null || d.promotedDesignation) && <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px] px-2 pb-2">
                  {d.hike != null && <div><div className="text-[12px] text-t3">Hike</div><span className="text-[14px] font-bold text-t0">{d.hike}%</span></div>}
                  {d.revisedCtc != null && <div><div className="text-[12px] text-t3">Revised CTC</div><span className="text-[14px] font-bold text-t0">₹{d.revisedCtc.toLocaleString("en-IN")}</span></div>}
                  {d.promotedDesignation && <div><div className="text-[12px] text-t3">Promoted Designation</div><span className="text-[14px] font-bold text-t0">{d.promotedDesignation}</span></div>}
                </div>}
              </div> : <div className="text-[14px] text-t3 p-2">Awaiting HR finalization</div>}
            </Cd>}
          </div>

          <div><Cd style={{ position: "sticky", top: 0 }}>
            <div className="text-[14px] font-bold text-t0 mb-[10px]">Rating Summary</div>
            {(d.selfView ? [["Self", d.sr, K.p]] : [["Self", d.sr, K.p], ["Manager", d.mr, K.bl], ["Centre Head", d.chr, K.tl], ["Final", d.fr, K.g]]).map(([l, v, c]) => (
              <div key={l} className="flex justify-between items-center py-[6px] border-b border-b0">
                <span className="text-[14px] text-t2">{l}</span>
                {v ? <span className="text-[15px] font-extrabold" style={{ color: c }}>{v}/5</span> : <span className="text-[14px] text-t3">—</span>}
              </div>
            ))}
            {d.rec && !d.selfView && <div className="mt-[10px]"><div className="text-[12px] text-t3">Recommendation</div><Bg c={d.rec === "Promotion" ? K.g : d.rec === "Increment" ? K.bl : d.rec === "PIP" ? K.r : K.a} style={{ marginTop: 4 }}>{d.rec}</Bg></div>}
            <div className="mt-[14px] text-[14px] font-semibold text-t2 mb-[6px]">Workflow</div>
            {["Pending", "Self Done", "Mgr Review", "CH Review", "Done"].map((s, i) => {
              const stList = ["Pending", "Self Done", "Mgr Review", "CH Review", "Done"];
              const curIdx = stList.indexOf(d.status);
              const isComplete = i <= curIdx; const isCurrent = i === curIdx;
              return <div key={s} className="flex items-center gap-[6px] py-[3px]">
                <div className="w-[10px] h-[10px] rounded-full flex-shrink-0" style={{ background: isComplete ? K.g : K.b1, border: isCurrent ? `2px solid ${K.p}` : "none" }} />
                <span className={`text-[12px] ${isComplete ? "text-t0" : "text-t3"} ${isCurrent ? "font-bold" : "font-normal"}`}>{s}</span>
              </div>;
            })}
          </Cd></div>
        </div>
      </div>
    );
  }

  const apTabs = ["my"];
  if (can('appraisal', 'team', null, 'view')) apTabs.push("team");
  // Centre HR is view-only and not tied to being anyone's manager, so it
  // only gets "All Appraisals" (scoped server-side to its centre) — not
  // "Team", which is a direct-report view that doesn't apply to this role.
  if (can('appraisal', 'all', null, 'view')) apTabs.push("all");

  const ApTabBar = () => (
    <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] mb-3 flex-wrap">
      {apTabs.map(t => (
        <button
          key={t}
          onClick={() => setApTab(t)}
          className={`px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 ${apTab === t ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}
        >
          {t === "my" ? "My Appraisal" : t === "team" ? "Team" : "All Appraisals"}
          {t === "team" && apTeam.length > 0 && <span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold" style={{ background: K.pg, color: K.p }}>{apTeam.length}</span>}
          {t === "all" && AP.length > 0 && <span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold bg-b1 text-t2">{AP.length}</span>}
        </button>
      ))}
    </div>
  );

  // The banner's own thread through the workflow — same status vocabulary
  // as the rest of the page, just phrased as a message to the employee
  // instead of a table label.
  const myRec = apMy[0];
  const myMsg = !myRec ? null
    : myRec.status === "Pending" ? "Your self-assessment is due — complete it to keep the cycle on track."
    : myRec.status === "Self Done" ? `Your self-assessment has been submitted. Awaiting review from ${myRec.mnm || "your manager"}.`
    : myRec.status === "Mgr Review" ? `Your manager has rated you ${myRec.mr}/5. Awaiting Centre Head endorsement.`
    : myRec.status === "CH Review" ? "Your appraisal has been endorsed by the Centre Head. Awaiting final HR sign-off."
    : myRec.status === "Done" ? `Your appraisal is finalized — final rating ${myRec.fr}/5.`
    : null;

  return (
    <div>
      <div className="flex justify-between items-center mb-[14px]">
        <h1 className="m-0 text-2xl font-extrabold text-t0">Appraisal</h1>
        <div className="flex items-center gap-2">
          {cyc && <Bg c={K.g} dot>{cyc.name}</Bg>}
          {canPickEmployee && (
            <Bt s="sm" icon={Plus} disabled={creatingAp} onClick={() => setShowNewAp(true)}>
              {creatingAp ? "Starting..." : "Open Appraisal for Employee"}
            </Bt>
          )}
        </div>
      </div>
      {showNewAp && <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setShowNewAp(false)}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[712px] shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
          <div className="text-base font-extrabold text-t0 mb-1">Open an Appraisal</div>
          <div className="text-[14px] text-t3 mb-4">Appraisals stay locked until you open one. The employee is mailed and can then fill in their self-assessment.</div>

          <div className="mb-3">
            <div className="text-[14px] text-t2 mb-[3px]">Employee</div>
            <SearchSelect options={employees.map(e => ({ v: e.id, l: e.nm, sub: e.id }))} value={newApTarget} onChange={setNewApTarget} placeholder="Search employee…" searchPlaceholder="Type a name or ID…" clearable />
          </div>
          <div className="flex gap-[6px]">
            <Bt full disabled={creatingAp || !newApTarget} onClick={() => createAppraisal(newApTarget)}>{creatingAp ? "Opening..." : "Open for This Employee"}</Bt>
            <Bt v="secondary" full onClick={() => setShowNewAp(false)}>Cancel</Bt>
          </div>
        </div>
      </div>}

      {cyc && <Cd style={{ marginBottom: 12 }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-b0">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-extrabold text-t0">{cyc.name}</span>
              <Bg c={K.g} sm dot>{cyc.status}</Bg>
            </div>
            <div className="text-[13px] text-t2 mt-[3px]">Self → Manager → Centre Head → HR/Admin sign-off</div>
          </div>
          {cyc.endDate && <div className="text-right flex-shrink-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Deadline</div>
            <div className="text-[14px] font-extrabold" style={{ color: K.r }}>{fmtDDMMYY(cyc.endDate)}</div>
          </div>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {cycleSteps.map((s, i) => {
            const isCurrent = i === cycleCurrentStep;
            return (
              <div key={s.l} className="p-3 rounded-xl border" style={{ borderColor: isCurrent ? K.p : K.b0, borderWidth: isCurrent ? 2 : 1, background: isCurrent ? `${K.p}08` : "transparent" }}>
                <div className="flex items-center justify-between mb-[6px]">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: isCurrent ? K.p : K.t3 }}>Step 0{i + 1}{isCurrent ? " · Current" : ""}</span>
                  <span className="text-[11px] font-extrabold px-2 py-[2px] rounded-full" style={{ background: `${s.c}14`, color: s.c }}>{s.pct}%</span>
                </div>
                <div className="text-[13px] font-bold text-t0">{s.l}</div>
                <div className="text-[11px] text-t3 mt-[2px]">{s.sub}</div>
                <div className="mt-[10px] w-full bg-bg2 h-[6px] rounded-full overflow-hidden"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${s.pct}%`, background: s.c }} /></div>
              </div>
            );
          })}
        </div>
      </Cd>}
      {!cyc && <Cd style={{ marginBottom: 10, padding: 20 }}><div className="text-center text-[14px] text-t3">No active appraisal cycle. Admin can activate one from Settings → Appraisal.</div></Cd>}

      <KpiStrip items={[
        { icon: Users,       label: "Eligible",  value: String(eligible.length), c: K.p  },
        { icon: PenLine,     label: "Self Done", value: String(selfDone.length), c: K.pu },
        { icon: BookOpen,    label: "Mgr Done",  value: String(mgrDone.length),  c: K.bl },
        { icon: Award,       label: "CH Done",   value: String(chDone.length),   c: K.tl },
        { icon: CheckCircle, label: "Complete",  value: String(done.length),     c: K.g  },
      ]} />

      {myRec && myMsg && <div className="rounded-2xl p-5 sm:p-6 mb-4 text-white relative overflow-hidden" style={{ background: `linear-gradient(120deg, ${K.t0}, #1e293b 55%, ${K.p})` }}>
        <div className="absolute -right-10 -bottom-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: `${K.p}33`, filter: "blur(60px)" }} />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-[11px] font-semibold text-white/80 border border-white/10 mb-3">
              <span className="w-[6px] h-[6px] rounded-full" style={{ background: K.g }} />
              <span>Your Appraisal — {cyc?.name}</span>
            </div>
            <div className="text-[16px] sm:text-[18px] font-bold text-white leading-snug">{myMsg}</div>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-[12px]">
              {myRec.sr != null && <div className="bg-white/10 px-3 py-1 rounded-lg border border-white/10"><span className="text-white/60">Self:</span> <span className="font-bold text-white">{myRec.sr}/5</span></div>}
              {myRec.mr != null && <div className="bg-white/10 px-3 py-1 rounded-lg border border-white/10"><span className="text-white/60">Manager:</span> <span className="font-bold text-white">{myRec.mr}/5</span></div>}
              {myRec.fr != null && <div className="bg-white/10 px-3 py-1 rounded-lg border border-white/10"><span className="text-white/60">Final:</span> <span className="font-bold text-white">{myRec.fr}/5</span></div>}
            </div>
          </div>
          <button
            onClick={() => { setApDet(myRec); setEditVals({}); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-t0 font-bold text-[13px] hover:bg-white/90 transition shadow-md flex-shrink-0 cursor-pointer border-0"
          >
            {myRec.status === "Pending" ? "Complete Self-Assessment" : "View My Submission"} <ArrowRight size={15} />
          </button>
        </div>
      </div>}

      <ApTabBar />

      {apTab === "my" && <div>
        {apMy.length > 0 ? apMy.map(a => (
          <Cd key={a.id} style={{ marginBottom: 10, cursor: "pointer", border: `1px solid ${stClrAp(a.status)}22` }} onClick={() => { setApDet(a); setEditVals({}); }}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2"><Av nm={a.enm} sz={30} /><div><div className="text-[14px] font-bold text-t0">{a.enm}</div><div className="text-[12px] text-t3">{a.rl} · {a.dept}</div></div></div>
              <div className="flex items-center gap-[10px]">
                {a.sr && <div className="text-center"><div className="text-[12px] text-t3">Self</div><div className="text-[17px] font-extrabold text-p">{a.sr}</div></div>}
                {a.mr && <div className="text-center"><div className="text-[12px] text-t3">Mgr</div><div className="text-[17px] font-extrabold text-bl">{a.mr}</div></div>}
                {a.fr && <div className="text-center"><div className="text-[12px] text-t3">Final</div><div className="text-[17px] font-extrabold text-g">{a.fr}</div></div>}
                <Bg c={stClrAp(a.status)} dot>{a.status}</Bg>
              </div>
            </div>
            {a.status === "Pending" && <div className="mt-2 p-2 rounded border text-[14px]" style={{ background: `${K.a}08`, borderColor: `${K.a}18`, color: K.a }}>Action required: Complete your self-assessment</div>}
          </Cd>
        )) : (
          <div className="text-center p-[30px] text-t3 text-[14px]">
            {canPickEmployee
              ? "No appraisal found for current cycle"
              : "Your appraisal isn't open yet. HR opens it once you complete the year — you'll get an email when it's your turn."}
          </div>
        )}
      </div>}

      {apTab === "team" && <div>
        <div className="mb-3 flex justify-between items-center">
          <span className="text-[14px] text-t2">{apTeam.filter(a => a.mr).length} of {apTeam.length} reviewed</span>
          <div className="w-[120px] h-[6px] rounded-[3px] bg-b1 overflow-hidden"><div className="h-full rounded-[3px] bg-g" style={{ width: apTeam.length > 0 ? (apTeam.filter(a => a.mr).length / apTeam.length * 100) + "%" : "0%" }} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="w-[220px]"><Inp icon={Search} placeholder="Filter team by name/role…" value={apSearch} onChange={setApSearch} /></div>
          <div className="w-[170px]"><Sl options={[{ v: "", l: "All Stages" }, ...Array.from(new Set(apTeam.map(r => r.status))).filter(Boolean).map(s => ({ v: s, l: s }))]} value={apStatusFilter} onChange={setApStatusFilter} /></div>
        </div>
        <Tbl cols={[
          { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.enm} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.enm}</div><div className="text-[12px] text-t3">{r.rl} · {r.dept}</div></div></div> },
          { k: "tenure", l: "Tenure", r: r => <span className="text-[12px] text-t2">{r.tenure ? Math.round(r.tenure / 30) + "m" : "—"}</span> },
          { k: "sr", l: "Self", r: r => r.sr ? <span className="font-bold text-p">{r.sr}/5</span> : <Bg c={K.a} sm>Pending</Bg> },
          { k: "mr", l: "Mgr", r: r => r.mr ? <span className="font-bold text-bl">{r.mr}/5</span> : <span className="text-[12px] text-t3">—</span> },
          { k: "rec", l: "Rec", r: r => r.rec ? <Bg c={r.rec === "Promotion" ? K.g : r.rec === "Increment" ? K.bl : r.rec === "PIP" ? K.r : K.t3} sm>{r.rec}</Bg> : <span className="text-[12px] text-t3">—</span> },
          { k: "st", l: "Status", r: r => <Bg c={stClrAp(r.status)} sm dot>{r.status}</Bg> },
          { k: "aw", l: "Awaiting", r: r => <span className="text-[12px] text-t2">{awaitingFor(r)}</span> },
          { k: "a", l: "", r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => { setApDet(r); setEditVals({}); }}>Review</Bt> }
        ]} data={apTeamFiltered} />
        {apTeamFiltered.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">{apTeam.length === 0 ? "No team members in this cycle" : "No appraisals match your search/filter"}</div>}
      </div>}

      {apTab === "all" && <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="w-[220px]"><Inp icon={Search} placeholder="Filter by name/dept…" value={apSearch} onChange={setApSearch} /></div>
          <div className="w-[170px]"><Sl options={[{ v: "", l: "All Stages" }, ...Array.from(new Set(AP.map(r => r.status))).filter(Boolean).map(s => ({ v: s, l: s }))]} value={apStatusFilter} onChange={setApStatusFilter} /></div>
        </div>
        <Tbl cols={[
          { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.enm} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.enm}</div><div className="text-[12px] text-t3">{r.dept} · {r.ofc}</div></div></div> },
          { k: "sr", l: "Self", r: r => r.sr ? <span className="font-bold text-p">{r.sr}/5</span> : <span className="text-[12px] text-t3">—</span> },
          { k: "mr", l: "Mgr", r: r => r.mr ? <span className="font-bold text-bl">{r.mr}/5</span> : <span className="text-[12px] text-t3">—</span> },
          { k: "chr", l: "CH", r: r => r.chr ? <span className="font-bold text-tl">{r.chr}/5</span> : <span className="text-[12px] text-t3">—</span> },
          { k: "fr", l: "Final", r: r => r.fr ? <span className="font-extrabold text-g">{r.fr}/5</span> : <span className="text-[12px] text-t3">—</span> },
          { k: "rec", l: "Rec", r: r => r.rec ? <Bg c={r.rec === "Promotion" ? K.g : r.rec === "Increment" ? K.bl : r.rec === "PIP" ? K.r : K.t3} sm>{r.rec}</Bg> : <span className="text-[12px] text-t3">—</span> },
          { k: "st", l: "Status", r: r => <Bg c={stClrAp(r.status)} sm dot>{r.status}</Bg> },
          { k: "aw", l: "Awaiting", r: r => <span className="text-[12px] text-t2">{awaitingFor(r)}</span> },
          { k: "a", l: "", r: r => <div className="flex gap-[3px]">
            <Bt v="ghost" s="xs" icon={Eye} onClick={() => { setApDet(r); setEditVals({}); }}>View</Bt>
            {r.status === "CH Review" && canEditHr(r) && <Bt v="success" s="xs" icon={Check} disabled={savingId === r.id} onClick={() => submitApprove(r.id)}>{savingId === r.id ? "Approving..." : "Approve"}</Bt>}
          </div> }
        ]} data={apAllFiltered} />
        {apAllFiltered.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">{AP.length === 0 ? "No appraisals found" : "No appraisals match your search/filter"}</div>}
      </div>}
    </div>
  );
};
